/**
 * Support Ticketing API (`api/tickets/*`).
 *
 * Backed by ASP.NET Core Support Tickets subsystem. Provides endpoints for student
 * ticket creation/viewing, staff inbox/triage/replies, and attachment handling.
 */
import { apiClient, ApiError } from "@/lib/api/client";
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";
import type { PaginatedResponse } from "@/lib/types";

// ── Enums ────────────────────────────────────────────────────────────────────

/** `LMSAPI.DAL.Enums.TicketStatus` */
export enum TicketStatus {
  Open = 0,
  InProgress = 1,
  Resolved = 2,
  Closed = 3,
}

/** `LMSAPI.DAL.Enums.TicketPriority` */
export enum TicketPriority {
  Low = 0,
  Normal = 1,
  High = 2,
  Critical = 3,
  /**
   * Written ONLY by the server's SLA sweep, never chosen by a person.
   * Overwrites the ticket's real severity, which is why `priorityBeforeEscalation` exists.
   */
  Escalated = 4,
}

/** `LMSAPI.DAL.Enums.TicketAuthorRole` */
export enum TicketAuthorRole {
  Student = 0,
  TeamLeader = 1,
  Admin = 2,
}

/** `LMSAPI.DAL.Enums.TicketAssigneeRole` */
export enum TicketAssigneeRole {
  TeamLeader = 0,
  TechSupport = 1,
}

/** `LMSAPI.BLL.Dtos.Ticket.TicketAssigneeScope` */
export enum TicketAssigneeScope {
  GroupLeader = 0,
  Administrator = 1,
  OutOfScope = 2,
}

/**
 * `LMSAPI.DAL.Enums.TrainingStatus` — the student's program-level lifecycle.
 *
 * Numeric, like every other enum on this surface: the API registers no JsonStringEnumConverter, so
 * System.Text.Json sends the ordinal. (`students.ts` types its own `trainingStatus` as a string
 * because that endpoint maps it to one explicitly — a different shape, not a different enum.)
 */
export enum TrainingStatus {
  InTraining = 0,
  Passed = 1,
  Failed = 2,
  WithdrawnDuringTraining = 3,
  WithdrawnAfterTraining = 4,
  NotRecommended = 5,
}

/** Human label for a {@link TrainingStatus}. */
export function trainingStatusLabel(status: TrainingStatus): string {
  switch (status) {
    case TrainingStatus.InTraining:
      return "In training";
    case TrainingStatus.Passed:
      return "Passed";
    case TrainingStatus.Failed:
      return "Failed";
    case TrainingStatus.WithdrawnDuringTraining:
      return "Withdrawn during training";
    case TrainingStatus.WithdrawnAfterTraining:
      return "Withdrawn after training";
    case TrainingStatus.NotRecommended:
      return "Not recommended";
    default:
      return "Unknown";
  }
}

/** `LMSAPI.DAL.Enums.TicketEventType` */
export enum TicketEventType {
  Created = 0,
  StatusChanged = 1,
  PriorityChanged = 2,
  AssigneeChanged = 3,
  Replied = 4,
  InternalNoteAdded = 5,
  FirstResponseRecorded = 6,
  FirstResponseSlaBreached = 7,
  ResolutionSlaBreached = 8,
  AutoEscalated = 9,
}

// ── Interfaces ───────────────────────────────────────────────────────────────

export interface TicketAttachment {
  url: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface TicketAssigneeCandidate {
  userId: string;
  fullName: string;
  email: string | null;
  scope: TicketAssigneeScope;
  isAssignable: boolean;
  groups: string[];
}

export interface TicketListItem {
  id: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  priorityBeforeEscalation: TicketPriority | null;
  topicId: string;
  topicTitle: string;
  studentUserId: string;
  studentFullName: string | null;
  studentEmail: string | null;
  assignedToUserId: string | null;
  assignedToFullName: string | null;
  createdAt: string;
  lastActivityAt: string;
  firstResponseDueAt: string;
  resolutionDueAt: string;
  firstRespondedAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  firstResponseBreachedAt: string | null;
  resolutionBreachedAt: string | null;
  escalatedAt: string | null;
  /** Student-visible messages only; internal notes are not counted. */
  messageCount: number;
}

export interface TicketMessage {
  id: string;
  authorUserId: string;
  authorFullName: string | null;
  authorRole: TicketAuthorRole;
  body: string;
  /** Staff-only. The server never sends a `true` row to a student. */
  isInternalNote: boolean;
  attachment: TicketAttachment | null;
  createdAt: string;
  editedAt: string | null;
}

export interface TicketEvent {
  id: string;
  eventType: TicketEventType;
  changedByUserId: string | null;
  /** Null means the SLA sweep acted as the system, not a person. */
  changedByFullName: string | null;
  oldStatus: TicketStatus | null;
  newStatus: TicketStatus | null;
  oldPriority: TicketPriority | null;
  newPriority: TicketPriority | null;
  detail: string | null;
  occurredAt: string;
}

/**
 * Who the filer actually is, beyond a name and an email — what a support agent needs in order to
 * route or escalate. Detail view only: every field costs a join through the student's group
 * memberships, which the board's list rows never read.
 *
 * Resolved from group membership, NOT the legacy free-text batch/team-lead columns on the user
 * record — those are import leftovers that go stale when a student is moved.
 */
export interface TicketStudentContext {
  /** Usually one; a student may sit in more than one batch. */
  batches: string[];
  groups: string[];
  /** Leaders of those groups — who a training question gets handed to. */
  teamLeaders: string[];
  studentCode: string | null;
  phoneNumber: string | null;
  trainingStatus: TrainingStatus;
}

export interface TicketDetail extends TicketListItem {
  description: string;
  /** Never null; empty lists when the student is in no group yet. */
  student: TicketStudentContext;
  attachment: TicketAttachment | null;
  messages: TicketMessage[];
  /** ALWAYS EMPTY for a student caller — the audit trail is internal. */
  events: TicketEvent[];
}

/** What the student's Create Ticket picker receives — routing policy is deliberately absent. */
export interface StudentTicketTopic {
  id: string;
  title: string;
  description: string | null;
}

export interface TicketQuery {
  status?: TicketStatus;
  priority?: TicketPriority;
  topicId?: string;
  assignedToUserId?: string;
  unassignedOnly?: boolean;
  breachedOnly?: boolean;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface MyTicketsQuery {
  status?: TicketStatus;
  pageNumber?: number;
  pageSize?: number;
}

export interface CreateTicketInput {
  topicId: string;
  subject: string;
  description: string;
  attachment?: TicketAttachment | null;
}

export interface AddTicketReplyInput {
  body: string;
  isInternalNote?: boolean;
  attachment?: TicketAttachment | null;
}

// ── Timestamp Normalization ──────────────────────────────────────────────────

/**
 * Backend serializes DateTimes with Kind=Unspecified (e.g. "2026-08-25T14:30:00").
 * The browser parses this as local time unless pinned to UTC with "Z".
 * Values already carrying Z or an offset are left untouched.
 */
function toUtcIso(dateString: string): string {
  if (!dateString || typeof dateString !== "string") return dateString;
  const hasTimezone = /[zZ]$/.test(dateString) || /[+-]\d{2}:?\d{2}$/.test(dateString);
  return hasTimezone ? dateString : `${dateString}Z`;
}

function toNullableUtcIso(dateString: string | null | undefined): string | null {
  return dateString ? toUtcIso(dateString) : null;
}

function normalizeListItem(rawTicket: TicketListItem): TicketListItem {
  return {
    ...rawTicket,
    createdAt: toUtcIso(rawTicket.createdAt),
    lastActivityAt: toUtcIso(rawTicket.lastActivityAt),
    firstResponseDueAt: toUtcIso(rawTicket.firstResponseDueAt),
    resolutionDueAt: toUtcIso(rawTicket.resolutionDueAt),
    firstRespondedAt: toNullableUtcIso(rawTicket.firstRespondedAt),
    resolvedAt: toNullableUtcIso(rawTicket.resolvedAt),
    closedAt: toNullableUtcIso(rawTicket.closedAt),
    firstResponseBreachedAt: toNullableUtcIso(rawTicket.firstResponseBreachedAt),
    resolutionBreachedAt: toNullableUtcIso(rawTicket.resolutionBreachedAt),
    escalatedAt: toNullableUtcIso(rawTicket.escalatedAt),
  };
}

function normalizeMessage(rawMessage: TicketMessage): TicketMessage {
  return {
    ...rawMessage,
    createdAt: toUtcIso(rawMessage.createdAt),
    editedAt: toNullableUtcIso(rawMessage.editedAt),
  };
}

function normalizeEvent(rawEvent: TicketEvent): TicketEvent {
  return {
    ...rawEvent,
    occurredAt: toUtcIso(rawEvent.occurredAt),
  };
}

function normalizeDetail(rawDetail: TicketDetail): TicketDetail {
  const baseNormalized = normalizeListItem(rawDetail);
  return {
    ...baseNormalized,
    description: rawDetail.description,
    attachment: rawDetail.attachment ?? null,
    // Defaulted rather than passed through: the server always sends this, but a payload cached by a
    // client that loaded before the field shipped has no `student`, and the panel reading it is
    // better served by empty lists than by a crash on `.length`.
    student: {
      batches: rawDetail.student?.batches ?? [],
      groups: rawDetail.student?.groups ?? [],
      teamLeaders: rawDetail.student?.teamLeaders ?? [],
      studentCode: rawDetail.student?.studentCode ?? null,
      phoneNumber: rawDetail.student?.phoneNumber ?? null,
      trainingStatus: rawDetail.student?.trainingStatus ?? TrainingStatus.InTraining,
    },
    messages: (rawDetail.messages ?? []).map(normalizeMessage),
    events: (rawDetail.events ?? []).map(normalizeEvent),
  };
}

// ── Query Builders ───────────────────────────────────────────────────────────

function buildMyTicketsQuery(params: MyTicketsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.status !== undefined) searchParams.append("status", params.status.toString());
  if (params.pageNumber !== undefined) searchParams.append("pageNumber", params.pageNumber.toString());
  if (params.pageSize !== undefined) searchParams.append("pageSize", params.pageSize.toString());
  return searchParams.toString();
}

function buildTicketsQuery(params: TicketQuery): string {
  const searchParams = new URLSearchParams();
  if (params.status !== undefined) searchParams.append("status", params.status.toString());
  if (params.priority !== undefined) searchParams.append("priority", params.priority.toString());
  if (params.topicId) searchParams.append("topicId", params.topicId);
  if (params.assignedToUserId) searchParams.append("assignedToUserId", params.assignedToUserId);
  if (params.unassignedOnly !== undefined) {
    searchParams.append("unassignedOnly", params.unassignedOnly.toString());
  }
  if (params.breachedOnly !== undefined) {
    searchParams.append("breachedOnly", params.breachedOnly.toString());
  }
  if (params.search) searchParams.append("search", params.search);
  if (params.pageNumber !== undefined) searchParams.append("pageNumber", params.pageNumber.toString());
  if (params.pageSize !== undefined) searchParams.append("pageSize", params.pageSize.toString());
  return searchParams.toString();
}

// ── API Functions ────────────────────────────────────────────────────────────

export async function fetchStudentTicketTopics(): Promise<StudentTicketTopic[]> {
  return apiClient.get<StudentTicketTopic[]>("/tickets/topics");
}

export async function createTicket(input: CreateTicketInput): Promise<TicketDetail> {
  const responsePayload = await apiClient.post<TicketDetail>("/tickets", input);
  return normalizeDetail(responsePayload);
}

export async function fetchMyTickets(
  params: MyTicketsQuery = {},
): Promise<PaginatedResponse<TicketListItem>> {
  const queryString = buildMyTicketsQuery(params);
  const endpoint = queryString ? `/tickets/my?${queryString}` : "/tickets/my";
  const responsePayload = await apiClient.get<PaginatedResponse<TicketListItem>>(endpoint);
  return {
    ...responsePayload,
    items: responsePayload.items.map(normalizeListItem),
  };
}

export async function fetchTickets(
  params: TicketQuery = {},
): Promise<PaginatedResponse<TicketListItem>> {
  const queryString = buildTicketsQuery(params);
  const endpoint = queryString ? `/tickets?${queryString}` : "/tickets";
  const responsePayload = await apiClient.get<PaginatedResponse<TicketListItem>>(endpoint);
  return {
    ...responsePayload,
    items: responsePayload.items.map(normalizeListItem),
  };
}

export async function fetchTicket(id: string): Promise<TicketDetail> {
  const responsePayload = await apiClient.get<TicketDetail>(`/tickets/${id}`);
  return normalizeDetail(responsePayload);
}

export async function addTicketReply(
  id: string,
  input: AddTicketReplyInput,
): Promise<TicketMessage> {
  const responsePayload = await apiClient.post<TicketMessage>(`/tickets/${id}/replies`, input);
  return normalizeMessage(responsePayload);
}

export async function changeTicketStatus(
  id: string,
  status: TicketStatus,
): Promise<TicketDetail> {
  const responsePayload = await apiClient.patch<TicketDetail>(`/tickets/${id}/status`, { status });
  return normalizeDetail(responsePayload);
}

export async function changeTicketPriority(
  id: string,
  priority: TicketPriority,
): Promise<TicketDetail> {
  const responsePayload = await apiClient.patch<TicketDetail>(`/tickets/${id}/priority`, { priority });
  return normalizeDetail(responsePayload);
}

export async function assignTicket(
  id: string,
  assignedToUserId: string | null,
): Promise<TicketDetail> {
  const responsePayload = await apiClient.patch<TicketDetail>(`/tickets/${id}/assignee`, {
    assignedToUserId,
  });
  return normalizeDetail(responsePayload);
}

export async function fetchAssignableStaff(
  ticketId: string,
): Promise<TicketAssigneeCandidate[]> {
  return apiClient.get<TicketAssigneeCandidate[]>(`/tickets/${ticketId}/assignable-staff`);
}

export async function uploadTicketAttachment(file: File): Promise<TicketAttachment> {
  if (!API_BASE_URL) throw new Error("Backend not configured (VITE_API_URL)");
  const formData = new FormData();
  formData.append("file", file);
  const headers = getAuthHeaders();
  delete headers["Content-Type"];
  const uploadResponse = await fetch(`${API_BASE_URL}/tickets/attachments`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!uploadResponse.ok) {
    const errorPayload = (await uploadResponse.json().catch(() => ({}))) as {
      error?: string;
      title?: string;
      message?: string;
    };
    throw new Error(
      errorPayload.error ??
        errorPayload.title ??
        errorPayload.message ??
        `Upload failed (${uploadResponse.status})`,
    );
  }
  const uploadedRecord = (await uploadResponse.json()) as TicketAttachment;
  return {
    url: uploadedRecord.url,
    fileName: uploadedRecord.fileName,
    contentType: uploadedRecord.contentType,
    sizeBytes: uploadedRecord.sizeBytes,
  };
}

/** Mirror the server's TicketAttachmentPolicy to prevent unnecessary round trips. */
export const TICKET_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const TICKET_ATTACHMENT_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".pdf",
  ".txt",
  ".log",
  ".csv",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
];

/**
 * Maps an HTTP status code to a user-friendly message when neither the server payload nor
 * `error.message` provided anything useful. 5xx is handled upstream by the api client
 * (which substitutes `SERVER_ISSUE_MESSAGE`), so it is intentionally absent here.
 */
export function statusDerivedMessage(status: number): string | undefined {
  if (status === 400) return "That change was rejected. Refresh the ticket and try again.";
  if (status === 401) return "Your session has expired. Sign in again to continue.";
  if (status === 403) return "You no longer have permission to do that on this ticket.";
  if (status === 404) return "This ticket no longer exists. It may have been deleted.";
  if (status === 409)
    return "Someone else changed this ticket while you were working on it. Reload to see the current state.";
  return undefined;
}

/** Generic placeholder the api client sets when the server returned no message. */
const GENERIC_CLIENT_MESSAGE = "API request failed";

export function ticketErrorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (error instanceof ApiError) {
    const errorPayload = error.data as { message?: string; error?: string; title?: string } | undefined;
    return (
      errorPayload?.message ??
      errorPayload?.error ??
      errorPayload?.title ??
      (error.message && error.message !== GENERIC_CLIENT_MESSAGE ? error.message : undefined) ??
      statusDerivedMessage(error.status) ??
      fallback
    );
  }
  return error instanceof Error ? error.message : fallback;
}
